import { ChevronRight } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { NAV_LABELS } from './nav-items';

/** `employee-history` → `Employee History`. */
function humanise(segment: string): string {
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Trail derived from the URL.
 *
 * A UUID segment renders as "Details" rather than the raw id — a 36-character
 * hex string in a breadcrumb tells the user nothing and wrecks the layout on
 * narrow screens. Pages that know the record's name should render their own
 * heading; this is the fallback.
 */
export function Breadcrumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  const crumbs = segments.map((segment, index) => {
    const to = `/${segments.slice(0, index + 1).join('/')}`;
    const label = UUID_PATTERN.test(segment) ? 'Details' : (NAV_LABELS[to] ?? humanise(segment));
    return { to, label, isLast: index === segments.length - 1 };
  });

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex items-center gap-1 text-sm text-muted-foreground">
        <li className="flex items-center gap-1">
          <Link to="/" className="rounded-sm transition-colors hover:text-foreground">
            Home
          </Link>
          <ChevronRight className="size-3.5 shrink-0" aria-hidden="true" />
        </li>

        {crumbs.map((crumb) => (
          <li key={crumb.to} className="flex min-w-0 items-center gap-1">
            {crumb.isLast ? (
              <span className="truncate font-medium text-foreground" aria-current="page">
                {crumb.label}
              </span>
            ) : (
              <>
                <Link to={crumb.to} className="truncate rounded-sm transition-colors hover:text-foreground">
                  {crumb.label}
                </Link>
                <ChevronRight className="size-3.5 shrink-0" aria-hidden="true" />
              </>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
